import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Eye,
  EyeOff,
  ExternalLink,
  KeyRound,
  Loader2,
  MapPinned,
  RefreshCw,
  Save,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import { Button } from "../../components/ui/button";
import { toast } from "../../components/ui/sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";

const KEY_RE = /^AIza[0-9A-Za-z_-]{20,100}$/;
const MAP_ID_RE = /^[0-9A-Za-z_-]{1,100}$/;

function FieldLabel({ children, hint }) {
  return (
    <div className="mb-1.5">
      <div className="text-sm font-semibold text-slate-700">{children}</div>
      {hint && <div className="mt-0.5 text-xs leading-5 text-slate-500">{hint}</div>}
    </div>
  );
}

export default function GoogleMapsSettings() {
  const { api, lang } = useApp();
  const zh = lang === "zh";
  const [current, setCurrent] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [mapId, setMapId] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [err, setErr] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);

  const load = useCallback(async () => {
    setErr("");
    try {
      const { data } = await api.get("/admin/google-maps/config");
      setCurrent(data);
      setApiKey(data.apiKey?.value || "");
      setMapId(data.mapId || "");
      setFieldError("");
    } catch (e) {
      setCurrent(null);
      setErr(e?.response?.data?.detail || e?.message || "Failed");
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const dirty = useMemo(() => {
    if (!current) return false;
    return apiKey.trim() !== String(current.apiKey?.value || "").trim()
      || mapId.trim() !== String(current.mapId || "").trim();
  }, [apiKey, current, mapId]);
  const legacyDemo = !!current?.apiKey?.legacyDemo;

  const save = async () => {
    if (!current || saving || !dirty) return;
    const nextKey = apiKey.trim();
    const nextMapId = mapId.trim();
    if (!nextKey && current.apiKey.set) {
      setFieldError(zh ? "如需移除当前 Key，请使用「清除 Key」按钮。" : "Use “Clear key” to remove the current key.");
      return;
    }
    if (nextKey && !KEY_RE.test(nextKey)) {
      setFieldError(zh ? "API Key 格式不正确，应以 AIza 开头。" : "The API key format is invalid.");
      return;
    }
    if (nextMapId && !MAP_ID_RE.test(nextMapId)) {
      setFieldError(zh ? "Map ID 只能包含字母、数字、下划线和短横线。" : "The Map ID contains invalid characters.");
      return;
    }

    setFieldError("");
    setSaving(true);
    try {
      const body = { mapId: nextMapId };
      if (nextKey !== String(current.apiKey?.value || "").trim()) body.apiKey = nextKey;
      await api.put("/admin/google-maps/config", body);
      toast.success(zh ? "地图配置已保存，正在重新加载" : "Map settings saved. Reloading.");
      window.setTimeout(() => window.location.reload(), 500);
    } catch (e) {
      setFieldError(e?.response?.data?.detail || (zh ? "保存失败，请重试。" : "Could not save the settings."));
      setSaving(false);
    }
  };

  const clearKey = async () => {
    if (clearing) return;
    setClearing(true);
    try {
      await api.put("/admin/google-maps/config", { apiKey: "__clear__" });
      toast.success(zh ? "Google Maps API Key 已清除" : "Google Maps API key cleared");
      window.setTimeout(() => window.location.reload(), 500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (zh ? "清除失败" : "Could not clear the key"));
      setClearing(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-4" data-testid="google-maps-settings-page">
      <div>
        <h1 className="flex items-center gap-2 text-xl font-black text-slate-900 md:text-2xl">
          <MapPinned className="h-6 w-6 text-brand-600" />
          {zh ? "地图配置" : "Google Maps"}
        </h1>
        <p className="mt-1 text-xs leading-5 text-slate-500 md:text-sm">
          {zh
            ? "统一维护项目地图、布局绘制与地址弹窗使用的 Google Maps 凭据。保存后刷新页面即可生效，无需重新发布。"
            : "Manage the Google Maps credentials used by project maps, layout drawing, and address dialogs. Changes take effect after an app reload."}
        </p>
      </div>

      {!current && !err && (
        <div className="flex min-h-48 items-center justify-center text-slate-400" data-testid="google-maps-settings-loading">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      )}

      {err && (
        <div role="alert" className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3" data-testid="google-maps-settings-error">
          <AlertTriangle className="h-5 w-5 shrink-0 text-amber-700" />
          <p className="min-w-0 flex-1 text-sm text-amber-900">
            {zh ? `地图配置读取失败：${err}` : `Could not load map settings: ${err}`}
          </p>
          <Button variant="outline" size="sm" onClick={load}>
            <RefreshCw className="h-4 w-4" />{zh ? "重试" : "Retry"}
          </Button>
        </div>
      )}

      {current && (
        <>
          <section className="rounded-xl border border-slate-200 bg-white shadow-soft" data-testid="google-maps-status" data-legacy-demo={legacyDemo ? "1" : "0"}>
            <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-4 py-3">
              <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${legacyDemo ? "bg-red-50 text-red-700" : current.configured ? "bg-brand-50 text-brand-700" : "bg-amber-50 text-amber-700"}`}>
                {legacyDemo ? <AlertTriangle className="h-5 w-5" /> : current.configured ? <CheckCircle2 className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-800">
                  {legacyDemo
                    ? (zh ? "旧演示 Key，生产不可用" : "Legacy demo key cannot be used in production")
                    : current.configured
                      ? (zh ? "地图 Key 已填写" : "Google Maps key is set")
                      : (zh ? "地图服务尚未配置" : "Google Maps is not configured")}
                </p>
                <p className="mt-0.5 text-xs text-slate-500">
                  {legacyDemo
                    ? (zh ? "演示 Key 额度耗尽后会停止加载，请整段替换为项目正式 Key。" : "The demo-key quota can stop maps from loading. Replace it with a production key.")
                    : current.configured
                      ? (zh ? `当前 Key：${current.apiKey.masked}。保存后请打开业务地图确认实际状态。` : `Current key: ${current.apiKey.masked}. Open a business map to verify it.`)
                      : (zh ? "填写 Key 后，各业务地图会恢复加载。" : "Add a key to restore business maps.")}
                </p>
              </div>
              <span className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-semibold ${legacyDemo ? "bg-red-50 text-red-700" : current.configured ? "bg-brand-50 text-brand-700" : "bg-amber-50 text-amber-700"}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${legacyDemo ? "bg-red-500" : current.configured ? "bg-brand-500" : "bg-amber-500"}`} />
                {legacyDemo ? (zh ? "需替换" : "Replace") : current.configured ? (zh ? "已填写" : "Set") : (zh ? "待配置" : "Required")}
              </span>
            </div>
            <div className="grid gap-3 px-4 py-3 text-xs text-slate-500 sm:grid-cols-2">
              <div>
                <span className="text-slate-400">{zh ? "最后更新：" : "Last updated: "}</span>
                {current.updatedAt ? new Date(current.updatedAt).toLocaleString() : (zh ? "尚未保存" : "Never")}
              </div>
              <div>
                <span className="text-slate-400">{zh ? "更新人：" : "Updated by: "}</span>
                {current.updatedBy || "—"}
              </div>
            </div>
          </section>

          <section className="rounded-xl border border-slate-200 bg-white shadow-soft">
            <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3">
              <KeyRound className="h-4 w-4 text-slate-500" />
              <h2 className="text-sm font-semibold text-slate-800">{zh ? "浏览器 Key" : "Browser key"}</h2>
            </div>
            <div className="space-y-4 p-4">
              <div className="rounded-lg bg-slate-50 px-3 py-2.5">
                <p className="text-xs text-slate-500">{zh ? "当前填写" : "Currently stored"}</p>
                <p className="mt-1 break-all font-mono text-sm text-slate-800" data-testid="google-maps-current-key">
                  {current.apiKey.set ? current.apiKey.masked : (zh ? "未配置" : "Not configured")}
                </p>
              </div>

              <div>
                <FieldLabel hint={current.apiKey.set
                  ? legacyDemo
                    ? (zh ? "当前预填的是旧演示 Key，请整段替换为项目正式 Key。" : "The legacy demo key is pre-filled. Replace it with a production key.")
                    : (zh ? "系统当前 Key 已预填；直接替换并保存即可。" : "The current key is pre-filled. Replace it to rotate the key.")
                  : (zh ? "填写 Google Maps JavaScript API Key 后保存。" : "Enter the Google Maps JavaScript API key and save.")}>
                  <label htmlFor="google-maps-api-key">{zh ? "更新 API Key" : "Update API key"}</label>
                </FieldLabel>
                <div className="relative">
                  <input
                    id="google-maps-api-key"
                    type={showKey ? "text" : "password"}
                    value={apiKey}
                    onChange={(e) => { setApiKey(e.target.value); setFieldError(""); }}
                    autoComplete="new-password"
                    spellCheck={false}
                    placeholder={zh ? "粘贴 Google Maps JavaScript API Key" : "Paste the Google Maps JavaScript API key"}
                    aria-invalid={!!fieldError}
                    aria-describedby={fieldError ? "google-maps-field-error" : undefined}
                    data-testid="google-maps-api-key-input"
                    className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 pr-11 font-mono text-sm text-slate-800 placeholder:font-sans placeholder:text-slate-400 focus-visible:border-brand-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-100"
                  />
                  <button
                    type="button"
                    onClick={() => setShowKey((value) => !value)}
                    aria-label={showKey ? (zh ? "隐藏 Key" : "Hide key") : (zh ? "显示 Key" : "Show key")}
                    title={showKey ? (zh ? "隐藏 Key" : "Hide key") : (zh ? "显示 Key" : "Show key")}
                    className="absolute right-1.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                    data-testid="google-maps-api-key-visibility"
                  >
                    {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div>
                <FieldLabel hint={zh ? "可选。用于 Google 高级地图与 3D 图层；普通卫星地图可留空。" : "Optional. Used for advanced map styling and 3D layers."}>
                  <label htmlFor="google-maps-map-id">{zh ? "Map ID" : "Map ID"}</label>
                </FieldLabel>
                <input
                  id="google-maps-map-id"
                  value={mapId}
                  onChange={(e) => { setMapId(e.target.value); setFieldError(""); }}
                  autoComplete="off"
                  spellCheck={false}
                  placeholder={zh ? "例如 8c2362b3d5f8d9e878dca642" : "e.g. 8c2362b3d5f8d9e878dca642"}
                  data-testid="google-maps-map-id-input"
                  className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 font-mono text-sm text-slate-800 placeholder:font-sans placeholder:text-slate-400 focus-visible:border-brand-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-100"
                />
              </div>

              {fieldError && (
                <p id="google-maps-field-error" role="alert" className="flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="google-maps-field-error">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />{fieldError}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
                <Button
                  type="button"
                  onClick={save}
                  disabled={!dirty || saving}
                  data-testid="google-maps-save"
                  className="min-w-28"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  {zh ? "保存配置" : "Save"}
                </Button>
                {current.apiKey.set && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button type="button" variant="outline" className="text-destructive hover:text-destructive" disabled={clearing}>
                        {clearing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                        {zh ? "清除 Key" : "Clear key"}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{zh ? "清除 Google Maps Key？" : "Clear Google Maps key?"}</AlertDialogTitle>
                        <AlertDialogDescription>
                          {zh ? "清除后所有业务地图会立即停止加载，直到重新配置有效 Key。" : "All business maps will stop loading until a valid key is configured again."}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{zh ? "取消" : "Cancel"}</AlertDialogCancel>
                        <AlertDialogAction onClick={clearKey} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                          {zh ? "确认清除" : "Clear"}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </div>
          </section>

          <section className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-start gap-3">
              <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-brand-600" />
              <div className="min-w-0">
                <h2 className="text-sm font-semibold text-slate-800">{zh ? "Google Cloud 设置核对" : "Google Cloud checklist"}</h2>
                <ul className="mt-2 space-y-1.5 text-xs leading-5 text-slate-500">
                  <li>{zh ? "确认 Maps JavaScript API 已启用。" : "Confirm Maps JavaScript API is enabled."}</li>
                  <li>{zh ? "确认 Key 已关联有效结算账户，且没有额度或项目限制。" : "Confirm the key is linked to active billing without project limits."}</li>
                  <li>{zh ? "为浏览器 Key 配置允许的 HTTPS 来源，不要使用旧的演示 Key。" : "Restrict the browser key to approved HTTPS origins and do not reuse demo keys."}</li>
                </ul>
                <a
                  href="https://console.cloud.google.com/google/maps-apis/credentials"
                  target="_blank"
                  rel="noreferrer"
                  className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-brand-700 hover:underline"
                >
                  {zh ? "打开 Google Cloud 凭据页面" : "Open Google Cloud credentials"}
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
